import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

/**
 * Integrations Controller
 *
 * Manages GA4 and GSC integrations for a website.
 * - Fetches current integration status
 * - Handles property/site assignment
 * - Supports creating new GA4 properties
 * - Handles GSC verification flow (DNS auto, meta tag)
 * - Real-time updates via ActionCable
 */
export default class extends Controller {
  static targets = [
    // GA4 targets
    'ga4Container',
    'ga4Loading',
    'ga4Assigned',
    'ga4Unassigned',
    'ga4PropertySelect',
    'ga4AssignBtn',
    'ga4RefreshBtn',
    'ga4PropertyName',
    'ga4MeasurementId',
    'ga4InstalledAt',
    'ga4CreateForm',
    'ga4NewPropertyName',
    'ga4GoogleAccountSelect',
    'ga4EmptyState',
    // GSC targets
    'gscContainer',
    'gscLoading',
    'gscVerified',
    'gscPending',
    'gscUnassigned',
    'gscSiteSelect',
    'gscAssignBtn',
    'gscRefreshBtn',
    'gscSiteUrl',
    'gscVerifiedAt',
    'gscPendingSiteUrl',
    'gscVerificationMethod',
    'gscVerificationInstructions',
    'gscInstructionsContent',
    'gscAddForm',
    'gscGoogleAccountSelect',
    'gscVerificationMethodRadio',
    'gscEmptyState'
  ]

  static values = {
    websiteId: String,
    ga4Url: String,
    gscUrl: String,
    statusUrl: String
  }

  connect() {
    console.log('[Integrations] Connected for website:', this.websiteIdValue)
    this.loadStatus()
    this.subscribeToChannel()
  }

  disconnect() {
    this.unsubscribeFromChannel()
  }

  // ==================== ActionCable ====================

  subscribeToChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: 'WebsiteIntegrationsChannel', website_id: this.websiteIdValue },
      {
        connected: () => console.log('[Integrations] ActionCable connected'),
        disconnected: () => console.log('[Integrations] ActionCable disconnected'),
        received: (data) => this.handleBroadcast(data)
      }
    )
  }

  unsubscribeFromChannel() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleBroadcast(data) {
    console.log('[Integrations] Broadcast:', data)

    switch (data.event) {
      case 'ga4_assigned':
      case 'ga4_created':
        this.loadGa4Status()
        this.showToast(data.message || 'GA4 property assigned', 'success')
        break
      case 'ga4_removed':
        this.loadGa4Status()
        this.showToast(data.message || 'GA4 property removed', 'info')
        break
      case 'gsc_assigned':
        this.loadGscStatus()
        this.showToast(data.message || 'GSC site assigned', 'success')
        break
      case 'gsc_verification_started':
        this.loadGscStatus()
        this.showToast('GSC verification started', 'info')
        break
      case 'gsc_verified':
        this.loadGscStatus()
        this.showToast('Site verified successfully!', 'success')
        break
      case 'gsc_verification_failed':
        this.loadGscStatus()
        this.showToast(data.message || 'Verification failed', 'error')
        break
      case 'gsc_removed':
        this.loadGscStatus()
        this.showToast(data.message || 'GSC site removed', 'info')
        break
      case 'properties_synced':
        this.loadGa4Properties()
        this.showToast('GA4 properties synced', 'success')
        break
      case 'sites_synced':
        this.loadGscSites()
        this.showToast('GSC sites synced', 'success')
        break
    }
  }

  // ==================== Initial Load ====================

  async loadStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) throw new Error('Failed to load status')

      const data = await response.json()
      this.renderGa4Status(data.ga4)
      this.renderGscStatus(data.gsc)
    } catch (error) {
      console.error('[Integrations] Load status error:', error)
      this.showToast('Failed to load integration status', 'error')
    }
  }

  async loadGa4Status() {
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) throw new Error('Failed to load status')

      const data = await response.json()
      this.renderGa4Status(data.ga4)
    } catch (error) {
      console.error('[Integrations] Load GA4 status error:', error)
    }
  }

  async loadGscStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) throw new Error('Failed to load status')

      const data = await response.json()
      this.renderGscStatus(data.gsc)
    } catch (error) {
      console.error('[Integrations] Load GSC status error:', error)
    }
  }

  // ==================== GA4 Rendering ====================

  renderGa4Status(ga4) {
    if (!this.hasGa4LoadingTarget) return

    this.ga4LoadingTarget.classList.add('hidden')

    if (ga4.assigned) {
      this.ga4AssignedTarget.classList.remove('hidden')
      this.ga4UnassignedTarget.classList.add('hidden')

      if (this.hasGa4PropertyNameTarget && ga4.property) {
        this.ga4PropertyNameTarget.textContent = ga4.property.display_name || ga4.property.name
      }
      if (this.hasGa4MeasurementIdTarget) {
        this.ga4MeasurementIdTarget.textContent = ga4.measurement_id || '—'
      }
      if (this.hasGa4InstalledAtTarget) {
        this.ga4InstalledAtTarget.textContent = ga4.installed_at ?
          new Date(ga4.installed_at).toLocaleDateString() :
          '—'
      }
    } else {
      this.ga4AssignedTarget.classList.add('hidden')
      this.ga4UnassignedTarget.classList.remove('hidden')
      this.loadGa4Properties()
    }
  }

  async loadGa4Properties() {
    try {
      const response = await fetch(this.ga4UrlValue, {
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) throw new Error('Failed to load properties')

      const data = await response.json()
      this.populateGa4Select(data.properties || [])
    } catch (error) {
      console.error('[Integrations] Load GA4 properties error:', error)
    }
  }

  populateGa4Select(properties) {
    if (!this.hasGa4PropertySelectTarget) return

    // Clear existing options except first
    this.ga4PropertySelectTarget.innerHTML = '<option value="">— Select a GA4 property —</option>'

    if (properties.length === 0) {
      if (this.hasGa4EmptyStateTarget) {
        this.ga4EmptyStateTarget.classList.remove('hidden')
      }
      return
    }

    if (this.hasGa4EmptyStateTarget) {
      this.ga4EmptyStateTarget.classList.add('hidden')
    }

    properties.forEach((prop) => {
      const option = document.createElement('option')
      option.value = prop.id
      option.textContent = `${prop.display_name || prop.name} (${prop.measurement_id || 'No data stream'})`
      this.ga4PropertySelectTarget.appendChild(option)
    })
  }

  // ==================== GSC Rendering ====================

  renderGscStatus(gsc) {
    if (!this.hasGscLoadingTarget) return

    this.gscLoadingTarget.classList.add('hidden')

    if (gsc.assigned && gsc.verified) {
      // Verified state
      this.gscVerifiedTarget.classList.remove('hidden')
      this.gscPendingTarget.classList.add('hidden')
      this.gscUnassignedTarget.classList.add('hidden')

      if (this.hasGscSiteUrlTarget && gsc.site) {
        this.gscSiteUrlTarget.textContent = gsc.site.site_url || gsc.site.domain
      }
      if (this.hasGscVerifiedAtTarget) {
        this.gscVerifiedAtTarget.textContent = gsc.verified_at ?
          new Date(gsc.verified_at).toLocaleDateString() :
          '—'
      }
    } else if (gsc.assigned && !gsc.verified) {
      // Pending verification state
      this.gscVerifiedTarget.classList.add('hidden')
      this.gscPendingTarget.classList.remove('hidden')
      this.gscUnassignedTarget.classList.add('hidden')

      if (this.hasGscPendingSiteUrlTarget && gsc.site) {
        this.gscPendingSiteUrlTarget.textContent = gsc.site.site_url || gsc.site.domain
      }
      if (this.hasGscVerificationMethodTarget) {
        this.gscVerificationMethodTarget.textContent = this.formatVerificationMethod(gsc.status)
      }
      this.loadGscVerificationStatus()
    } else {
      // Unassigned state
      this.gscVerifiedTarget.classList.add('hidden')
      this.gscPendingTarget.classList.add('hidden')
      this.gscUnassignedTarget.classList.remove('hidden')
      this.loadGscSites()
    }
  }

  formatVerificationMethod(status) {
    const methods = {
      pending_dns: 'DNS TXT Record',
      pending_meta_tag: 'Meta Tag',
      pending: 'Pending'
    }
    return methods[status] || status || 'Unknown'
  }

  async loadGscSites() {
    try {
      const response = await fetch(this.gscUrlValue, {
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) throw new Error('Failed to load sites')

      const data = await response.json()
      this.populateGscSelect(data.sites || [])
    } catch (error) {
      console.error('[Integrations] Load GSC sites error:', error)
    }
  }

  populateGscSelect(sites) {
    if (!this.hasGscSiteSelectTarget) return

    this.gscSiteSelectTarget.innerHTML = '<option value="">— Select a GSC site —</option>'

    if (sites.length === 0) {
      if (this.hasGscEmptyStateTarget) {
        this.gscEmptyStateTarget.classList.remove('hidden')
      }
      return
    }

    if (this.hasGscEmptyStateTarget) {
      this.gscEmptyStateTarget.classList.add('hidden')
    }

    sites.forEach((site) => {
      const option = document.createElement('option')
      option.value = site.id
      const verifiedBadge = site.verified ? ' [Verified]' : ''
      option.textContent = `${site.site_url || site.domain}${verifiedBadge}`
      this.gscSiteSelectTarget.appendChild(option)
    })
  }

  async loadGscVerificationStatus() {
    try {
      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/gsc_verification_status`,
        { headers: { Accept: 'application/json' } }
      )

      if (!response.ok) throw new Error('Failed to load verification status')

      const data = await response.json()

      if (this.hasGscInstructionsContentTarget) {
        if (data.manual_instructions) {
          this.gscInstructionsContentTarget.innerHTML = this.formatDnsInstructions(data.manual_instructions)
        } else if (data.status === 'pending_meta_tag') {
          this.gscInstructionsContentTarget.innerHTML = `
            <p>A verification meta tag has been added to your site.</p>
            <p class="mt-2"><strong>Next step:</strong> Deploy your website to complete verification.</p>
          `
        } else {
          this.gscInstructionsContentTarget.innerHTML = `
            <p>Verification is being processed. This may take a few minutes.</p>
          `
        }
      }
    } catch (error) {
      console.error('[Integrations] Load verification status error:', error)
    }
  }

  formatDnsInstructions(instructions) {
    if (!instructions) return ''
    return `
      <p class="mb-2">${instructions.message || 'Add the following DNS record:'}</p>
      <div class="grid grid-cols-3 gap-2 font-mono text-xs">
        <div><span class="text-caption">Type:</span> ${instructions.type || 'TXT'}</div>
        <div><span class="text-caption">Name:</span> ${instructions.name || '@'}</div>
        <div><span class="text-caption">Value:</span> ${instructions.value || ''}</div>
      </div>
    `
  }

  // ==================== GA4 Actions ====================

  onGa4PropertyChange() {
    const hasValue = this.ga4PropertySelectTarget.value !== ''
    this.ga4AssignBtnTarget.disabled = !hasValue
  }

  async assignGa4() {
    const propertyId = this.ga4PropertySelectTarget.value
    if (!propertyId) return

    this.ga4AssignBtnTarget.disabled = true
    this.ga4AssignBtnTarget.textContent = 'Assigning...'

    try {
      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/assign_analytics_property`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({ property_id: propertyId })
        }
      )

      const data = await response.json()

      if (data.success) {
        this.showToast('GA4 property assigned successfully', 'success')
        this.loadGa4Status()
      } else {
        throw new Error(data.error || 'Failed to assign property')
      }
    } catch (error) {
      console.error('[Integrations] Assign GA4 error:', error)
      this.showToast(error.message || 'Failed to assign GA4 property', 'error')
    } finally {
      this.ga4AssignBtnTarget.disabled = false
      this.ga4AssignBtnTarget.textContent = 'Assign'
    }
  }

  toggleGa4CreateForm() {
    if (this.hasGa4CreateFormTarget) {
      this.ga4CreateFormTarget.classList.toggle('hidden')
    }
  }

  async createGa4Property() {
    const googleAccountId = this.ga4GoogleAccountSelectTarget?.value
    const displayName = this.ga4NewPropertyNameTarget?.value || ''

    if (!googleAccountId) {
      this.showToast('Please select a Google account', 'error')
      return
    }

    try {
      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/create_analytics_property`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({
            google_account_id: googleAccountId,
            display_name: displayName
          })
        }
      )

      const data = await response.json()

      if (data.success) {
        this.showToast('GA4 property created and assigned', 'success')
        this.toggleGa4CreateForm()
        this.loadGa4Status()
      } else {
        throw new Error(data.error || 'Failed to create property')
      }
    } catch (error) {
      console.error('[Integrations] Create GA4 error:', error)
      this.showToast(error.message || 'Failed to create GA4 property', 'error')
    }
  }

  async removeGa4() {
    if (!confirm('Are you sure you want to remove GA4 tracking from this website?')) {
      return
    }

    try {
      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/remove_analytics_property`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': this.csrfToken
          }
        }
      )

      const data = await response.json()

      if (data.success) {
        this.showToast('GA4 property removed', 'info')
        this.loadGa4Status()
      } else {
        throw new Error(data.error || 'Failed to remove property')
      }
    } catch (error) {
      console.error('[Integrations] Remove GA4 error:', error)
      this.showToast(error.message || 'Failed to remove GA4 property', 'error')
    }
  }

  async refreshGa4() {
    this.ga4RefreshBtnTarget.disabled = true

    try {
      // Get the first Google account with analytics scope
      const select = this.ga4GoogleAccountSelectTarget
      const googleAccountId = select?.value || select?.options[0]?.value

      if (!googleAccountId) {
        this.showToast('No Google account with Analytics access', 'error')
        return
      }

      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/sync_google_account`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({ google_account_id: googleAccountId })
        }
      )

      const data = await response.json()

      if (data.success) {
        this.showToast('Syncing GA4 properties...', 'info')
      } else {
        throw new Error(data.error || 'Failed to start sync')
      }
    } catch (error) {
      console.error('[Integrations] Refresh GA4 error:', error)
      this.showToast(error.message || 'Failed to refresh', 'error')
    } finally {
      this.ga4RefreshBtnTarget.disabled = false
    }
  }

  // ==================== GSC Actions ====================

  onGscSiteChange() {
    const hasValue = this.gscSiteSelectTarget.value !== ''
    this.gscAssignBtnTarget.disabled = !hasValue
  }

  async assignGsc() {
    const siteId = this.gscSiteSelectTarget.value
    if (!siteId) return

    this.gscAssignBtnTarget.disabled = true
    this.gscAssignBtnTarget.textContent = 'Assigning...'

    try {
      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/assign_search_console_site`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({ site_id: siteId })
        }
      )

      const data = await response.json()

      if (data.success) {
        this.showToast('GSC site assigned successfully', 'success')
        this.loadGscStatus()
      } else {
        throw new Error(data.error || 'Failed to assign site')
      }
    } catch (error) {
      console.error('[Integrations] Assign GSC error:', error)
      this.showToast(error.message || 'Failed to assign GSC site', 'error')
    } finally {
      this.gscAssignBtnTarget.disabled = false
      this.gscAssignBtnTarget.textContent = 'Assign'
    }
  }

  toggleGscAddForm() {
    if (this.hasGscAddFormTarget) {
      this.gscAddFormTarget.classList.toggle('hidden')
    }
  }

  async addGscSite() {
    const googleAccountId = this.gscGoogleAccountSelectTarget?.value
    const verificationMethod = this.getSelectedVerificationMethod()

    if (!googleAccountId) {
      this.showToast('Please select a Google account', 'error')
      return
    }

    try {
      // First add the site
      const addResponse = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/add_search_console_site`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({ google_account_id: googleAccountId })
        }
      )

      const addData = await addResponse.json()

      if (!addData.success) {
        throw new Error(addData.error || 'Failed to add site')
      }

      // If meta tag verification was selected and site needs verification
      if (verificationMethod === 'meta_tag' && addData.needs_verification) {
        await this.initiateMetaTagVerification()
      }

      this.showToast('GSC site added', 'success')
      this.toggleGscAddForm()
      this.loadGscStatus()
    } catch (error) {
      console.error('[Integrations] Add GSC error:', error)
      this.showToast(error.message || 'Failed to add GSC site', 'error')
    }
  }

  getSelectedVerificationMethod() {
    const radios = this.gscVerificationMethodRadioTargets
    for (const radio of radios) {
      if (radio.checked) {
        return radio.value
      }
    }
    return 'dns_auto'
  }

  async initiateMetaTagVerification() {
    try {
      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/verify_gsc_via_meta_tag`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': this.csrfToken
          }
        }
      )

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to initiate meta tag verification')
      }

      this.showToast('Meta tag verification initiated. Deploy your site to complete.', 'info')
    } catch (error) {
      console.error('[Integrations] Meta tag verification error:', error)
      this.showToast(error.message || 'Failed to start verification', 'error')
    }
  }

  async checkGscVerification() {
    try {
      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/check_gsc_verification`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': this.csrfToken
          }
        }
      )

      const data = await response.json()

      if (data.success) {
        if (data.verified) {
          this.showToast('Site verified successfully!', 'success')
        } else {
          this.showToast('Verification still pending', 'info')
        }
        this.loadGscStatus()
      } else {
        throw new Error(data.error || 'Failed to check verification')
      }
    } catch (error) {
      console.error('[Integrations] Check verification error:', error)
      this.showToast(error.message || 'Failed to check verification', 'error')
    }
  }

  async removeGsc() {
    if (!confirm('Are you sure you want to remove Search Console from this website?')) {
      return
    }

    try {
      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/remove_search_console_site`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': this.csrfToken
          }
        }
      )

      const data = await response.json()

      if (data.success) {
        this.showToast('GSC site removed', 'info')
        this.loadGscStatus()
      } else {
        throw new Error(data.error || 'Failed to remove site')
      }
    } catch (error) {
      console.error('[Integrations] Remove GSC error:', error)
      this.showToast(error.message || 'Failed to remove GSC site', 'error')
    }
  }

  async refreshGsc() {
    this.gscRefreshBtnTarget.disabled = true

    try {
      const select = this.gscGoogleAccountSelectTarget
      const googleAccountId = select?.value || select?.options[0]?.value

      if (!googleAccountId) {
        this.showToast('No Google account with Search Console access', 'error')
        return
      }

      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/sync_google_account`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({ google_account_id: googleAccountId })
        }
      )

      const data = await response.json()

      if (data.success) {
        this.showToast('Syncing GSC sites...', 'info')
      } else {
        throw new Error(data.error || 'Failed to start sync')
      }
    } catch (error) {
      console.error('[Integrations] Refresh GSC error:', error)
      this.showToast(error.message || 'Failed to refresh', 'error')
    } finally {
      this.gscRefreshBtnTarget.disabled = false
    }
  }

  // ==================== Google Account Sync ====================

  async syncGoogleAccount(event) {
    const googleAccountId = event.currentTarget.dataset.googleAccountId
    if (!googleAccountId) return

    const button = event.currentTarget
    button.disabled = true

    try {
      const response = await fetch(
        `/admin/websites/${this.websiteIdValue}/integrations/sync_google_account`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({ google_account_id: googleAccountId })
        }
      )

      const data = await response.json()

      if (data.success) {
        const syncing = data.syncing || []
        const message = syncing.length > 0 ? `Syncing: ${syncing.join(', ')}` : 'Sync started'
        this.showToast(message, 'info')
      } else {
        throw new Error(data.error || 'Failed to start sync')
      }
    } catch (error) {
      console.error('[Integrations] Sync error:', error)
      this.showToast(error.message || 'Failed to sync', 'error')
    } finally {
      button.disabled = false
    }
  }

  // ==================== Helpers ====================

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  showToast(message, type = 'info') {
    // Remove existing toasts
    document.querySelectorAll('.integrations-toast').forEach((t) => t.remove())

    const toast = document.createElement('div')
    toast.className = `integrations-toast integrations-toast-${type}`

    const icons = {
      success:
        '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      error:
        '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      info: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>'
    }

    toast.innerHTML = `
      <div class="integrations-toast-content">
        ${icons[type] || icons.info}
        <span>${message}</span>
      </div>
    `

    this.ensureToastStyles()
    document.body.appendChild(toast)

    requestAnimationFrame(() => {
      toast.classList.add('integrations-toast-visible')
    })

    setTimeout(() => {
      toast.classList.remove('integrations-toast-visible')
      setTimeout(() => toast.remove(), 300)
    }, 4000)
  }

  ensureToastStyles() {
    if (document.getElementById('integrations-toast-styles')) return

    const style = document.createElement('style')
    style.id = 'integrations-toast-styles'
    style.textContent = `
      .integrations-toast {
        position: fixed;
        bottom: 24px;
        right: 24px;
        z-index: 10000;
        transform: translateY(16px);
        opacity: 0;
        transition: transform 0.3s ease, opacity 0.3s ease;
        pointer-events: none;
      }
      .integrations-toast-visible {
        transform: translateY(0);
        opacity: 1;
        pointer-events: auto;
      }
      .integrations-toast-content {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05);
      }
      .integrations-toast-success .integrations-toast-content {
        background: #ecfdf5;
        color: #065f46;
      }
      .integrations-toast-error .integrations-toast-content {
        background: #fef2f2;
        color: #991b1b;
      }
      .integrations-toast-info .integrations-toast-content {
        background: #eff6ff;
        color: #1e40af;
      }
    `
    document.head.appendChild(style)
  }
}
